import { useMemo, useState } from 'react'
import { ArrowRight, Scissors, SplitSquareHorizontal } from 'lucide-react'
import { charCount, splitIntoBlocks, summaryFromContent } from '../lib/utils'
import { CARD_COLUMNS } from '../data'
import { ModalShell } from './Shared'

export default function SplitModal({ card, onClose, onSplit }) {
  const blocks = useMemo(() => splitIntoBlocks(card.content), [card.content])
  const [selected, setSelected] = useState(() => {
    if (blocks.length <= 1) return new Set(blocks.map((_, index) => index))
    const start = Math.ceil(blocks.length / 2)
    return new Set(blocks.map((_, index) => index).slice(start))
  })
  const [title, setTitle] = useState(card.title + ' · 续')
  const [error, setError] = useState('')

  const movedText = blocks.filter((_, index) => selected.has(index)).join('\n\n')
  const leftText = blocks.filter((_, index) => !selected.has(index)).join('\n\n')
  const movedWords = charCount(movedText)

  function toggle(index) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
    setError('')
  }

  function submit() {
    if (!selected.size) { setError('请至少选择一段，移入新的卡片。'); return }
    if (!movedText.trim()) { setError('被选中的内容还是空的。'); return }
    if (!title.trim()) { setError('给新卡片起个名字吧。'); return }
    const newCard = {
      title: title.trim(),
      content: movedText,
      summary: summaryFromContent(movedText, '从上一张卡片里拆分出来。'),
      tags: [...(card.tags || [])].slice(0, 2),
      act: card.act,
      column: card.column,
      color: card.color,
      notes: '',
    }
    onSplit({ leftContent: leftText, newCard })
  }

  return (
    <ModalShell onClose={onClose} width="900px" className="split-modal">
      <div className="modal-topline"><span><Scissors size={14} /> SPLIT CARD</span><em>把一张卡片，拆成两个独立的呼吸</em></div>
      <div className="split-head">
        <div>
          <h2>拆开这一页。</h2>
          <p>选择要移入新卡片的段落。原卡片会保留未选中的内容，新卡片会继承相同的阶段与幕。</p>
        </div>
        <div className="split-source">
          <span>来源</span>
          <strong>《{card.title}》</strong>
          <em>{blocks.length} 个段落 · {charCount(card.content)} 字</em>
        </div>
      </div>

      <div className="split-grid">
        <div className="split-picker">
          <div className="split-picker__head">
            <span>选择要移走的段落</span>
            <button onClick={() => setSelected(new Set(blocks.map((_, index) => index)))}>全选</button>
          </div>
          <div className="split-blocks">
            {blocks.map((block, index) => {
              const isChecked = selected.has(index)
              return (
                <button
                  key={index}
                  className={'split-block ' + (isChecked ? 'is-checked' : '')}
                  onClick={() => toggle(index)}
                  type="button"
                >
                  <span className="split-block__check">{isChecked ? '✓' : ''}</span>
                  <span className="split-block__body">
                    <em>段落 {String(index + 1).padStart(2, '0')}</em>
                    <p>{block}</p>
                  </span>
                  <ArrowRight size={14} />
                </button>
              )
            })}
          </div>
          {leftText.trim() ? (
            <div className="split-left-note"><SplitSquareHorizontal size={13} /> 原卡片将留下 {charCount(leftText)} 字</div>
          ) : (
            <div className="split-left-note is-warn"><SplitSquareHorizontal size={13} /> 原卡片会变成空白，建议至少留下一段。</div>
          )}
        </div>

        <div className="split-preview">
          <div className="split-preview__label">新卡片预览</div>
          <div className="split-new-card">
            <div className="split-new-card__top">
              <span>NEW CARD</span>
              <em>{CARD_COLUMNS.find((column) => column.id === card.column)?.label}</em>
            </div>
            <input value={title} onChange={(event) => { setTitle(event.target.value); setError('') }} placeholder="新卡片标题" aria-label="新卡片标题" />
            <p>{movedText || '选中左侧段落后，内容会出现在这里。'}</p>
            <div className="split-new-card__foot">
              <span>{movedWords} 字</span>
              <span>继承标签 {(card.tags || []).slice(0, 2).join(' / ') || '无'}</span>
            </div>
          </div>
          <div className="split-tip">拆分不是复制。它会从原卡片中取走选中的段落，让两边各自独立生长。</div>
        </div>
      </div>

      {error ? <div className="form-error split-error">{error}</div> : null}
      <div className="modal-footer">
        <span className="modal-footer__hint">还可以在拆分后，到关联视图为两张卡片补一条线。</span>
        <div>
          <button className="btn btn--ghost" onClick={onClose}>取消</button>
          <button className="btn btn--ink" onClick={submit} disabled={!leftText.trim() || !movedText.trim()}>确认拆分 <Scissors size={15} /></button>
        </div>
      </div>
    </ModalShell>
  )
}
